import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { LoaderCircle } from "lucide-react";
import { useLayoutEffect } from "react";

export function EditableText({
   value,
   onConfirm,
   onComplete,
   className = "",
   placeholder = "Digite um texto...",
   disabled = false,
}) {
   const [isEditing, setIsEditing] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [text, setText] = useState(value);
   const inputRef = useRef(null);
   const measureRef = useRef(null);
   const [inputWidth, setInputWidth] = useState(1);

   useLayoutEffect(() => {
      if (measureRef.current) {
         setInputWidth(measureRef.current.getBoundingClientRect().width);
      }
   }, [text, isEditing, className]);

   useEffect(() => {
      setText(value);
   }, [value]);

   useEffect(() => {
      if (isEditing) {
         inputRef.current?.focus();
         inputRef.current?.select();
      }
   }, [isEditing]);

   async function handleConfirm() {
      if (isSaving) return;

      if (text === value) {
         setIsEditing(false);
         return;
      }

      if (!onConfirm) {
         setIsEditing(false);
         onComplete?.(text);
         return;
      }

      setIsSaving(true);

      try {
         await onConfirm(text);

         setIsEditing(false);
         onComplete?.(text);
      } catch (error) {
         // Mantém o campo aberto para permitir uma nova tentativa.
         console.error("Erro ao confirmar edição:", error);
      } finally {
         setIsSaving(false);
      }
   }

   function handleKeyDown(event) {
      if (event.key === "Enter") {
         event.preventDefault();
         handleConfirm();
      }

      if (event.key === "Escape" && !isSaving) {
         setText(value);
         setIsEditing(false);
      }
   }

   if (isEditing) {
      return (
         <div className="inline-flex max-w-full items-center gap-2">
            <span
               ref={measureRef}
               className={cn(
                  "pointer-events-none invisible absolute h-0 whitespace-pre",
                  "border-none p-0 font-inherit text-inherit",
                  className
               )}
               aria-hidden="true"
            >
               {text || placeholder || " "}
            </span>

            <input
               ref={inputRef}
               type="text"
               value={text}
               placeholder={placeholder}
               disabled={disabled || isSaving}
               onChange={(event) => setText(event.target.value)}
               onBlur={handleConfirm}
               onKeyDown={handleKeyDown}
               style={{
                  width: `${Math.max(inputWidth, 8)}px`,
               }}
               className={cn(`
               min-w-[1ch] max-w-full border-none! bg-transparent! p-0!
               text-inherit outline-none shadow-none ring-0
               focus:border-none focus:outline-none focus:ring-0
               focus:shadow-none ${isSaving ? 'animate-pulse' : ''}
            `, className)}
            />

            {isSaving && (
               <LoaderCircle
                  className="size-4 shrink-0 animate-spin text-muted-foreground"
                  aria-label="Salvando..."
               />
            )}
         </div>
      );
   }

   return (
      <span
         onClick={() => !disabled && setIsEditing(true)}
         className={cn(
            "inline-block rounded px-2 py-1",
            disabled
               ? "cursor-default"
               : "cursor-text hover:bg-(--borderColor)/50",
            className
         )}
         title={disabled ? undefined : "Clique para editar"}
      >
         {value || (
            <span className="text-gray-400">{placeholder}</span>
         )}
      </span>
   );
}